// Shared network constants: local dev URLs/ports and the generic URL query-key
// names used by the shared table state. Dependency-free; safe to import from
// any package.

import type {ApiEnvironment} from './environments';

/** Default API base URL when `VITE_API_URL` is unset (local dev). */
export const DEV_API_URL = 'http://localhost:4000';

/** Root domain every first-party portal + the API live under. */
const ROOT_DOMAIN = 'cbs-infinity.com';

/**
 * Client portal subdomain prefixes (the `apps/client-*` portals). In non-prod
 * environments each portal is served at `<prefix>-<env>.cbs-infinity.com` and
 * the internal portal at `<env>.cbs-infinity.com`. This mirrors the CDK
 * `ClientStack` domains in `cdk/bin/cbs-infinity.ts` — keep the two in sync
 * whenever a client portal is added or removed.
 */
const CLIENT_PORTAL_SUBDOMAINS = [
  'healthcues',
  'synrgy',
  'champion-health',
  'onhealth',
  'quantum',
] as const;

/**
 * Prod vanity domains → the portal's subdomain prefix on cbs-infinity.com.
 * Used to translate a canonical (prod) `Client.domain` stored in the DB into
 * the equivalent portal host of a non-prod environment.
 */
const VANITY_DOMAIN_PREFIXES: Record<string, (typeof CLIENT_PORTAL_SUBDOMAINS)[number]> = {
  'portal.synrgyhealth.com': 'synrgy',
  'portal.quantumhb.com': 'quantum',
  'portal.healthcues.com': 'healthcues',
};

/**
 * CTPA-branded internal portal (CDK `InternalPortalCtpaStack`). Served
 * alongside `cbs-infinity.com` in prod and `staging.cbs-infinity.com` in
 * staging; dev and vbatest have no CTPA stack.
 */
const CTPA_INTERNAL_PORTAL_DOMAIN = 'portal.ctpa.com';
const CTPA_INTERNAL_PORTAL_STAGING_DOMAIN = `staging.${CTPA_INTERNAL_PORTAL_DOMAIN}`;

/** Production portal origins, including the custom + legacy vanity domains. */
const PROD_PORTAL_ORIGINS: readonly string[] = [
  `https://${ROOT_DOMAIN}`, // internal portal
  `https://${CTPA_INTERNAL_PORTAL_DOMAIN}`, // internal portal
  `https://healthcues.${ROOT_DOMAIN}`,
  'https://portal.healthcues.com',
  `https://synrgy.${ROOT_DOMAIN}`,
  'https://portal.synrgyhealth.com',
  `https://champion-health.${ROOT_DOMAIN}`,
  `https://onhealth.${ROOT_DOMAIN}`,
  `https://quantum.${ROOT_DOMAIN}`,
  'https://portal.quantumhb.com',
];

/**
 * Resolve the portal host to use for outbound links (emails, Asana tickets)
 * in a given environment.
 *
 * `Client.domain` rows hold the canonical *production* domain (e.g.
 * `portal.synrgyhealth.com`), even in non-prod databases. On deployed
 * non-prod environments (dev / staging / vbatest) links must instead point at
 * that environment's portal — `<prefix>-<env>.cbs-infinity.com` for client
 * portals and `<env>.cbs-infinity.com` for the internal portal (the same CDK
 * `ClientStack` scheme as `getTrustedPortalOrigins`).
 *
 * `prod` and `local` return the domain unchanged. Unknown custom domains
 * (no vanity mapping, not under cbs-infinity.com) are also returned unchanged.
 */
export function resolvePortalHost(environment: ApiEnvironment, domain: string): string {
  if (environment === 'prod' || environment === 'local') return domain;

  const host = domain.toLowerCase();
  // Already scoped to this environment.
  if (host === `${environment}.${ROOT_DOMAIN}` || host.endsWith(`-${environment}.${ROOT_DOMAIN}`)) {
    return host;
  }
  // Internal portal apex.
  if (host === ROOT_DOMAIN) return `${environment}.${ROOT_DOMAIN}`;
  if (host === CTPA_INTERNAL_PORTAL_DOMAIN) {
    return environment === 'staging'
      ? CTPA_INTERNAL_PORTAL_STAGING_DOMAIN
      : `${environment}.${ROOT_DOMAIN}`;
  }

  const vanityPrefix = VANITY_DOMAIN_PREFIXES[host];
  if (vanityPrefix) return `${vanityPrefix}-${environment}.${ROOT_DOMAIN}`;

  // `<prefix>.cbs-infinity.com` (prod-style first-party portal domain).
  if (host.endsWith(`.${ROOT_DOMAIN}`)) {
    const prefix = host.slice(0, -`.${ROOT_DOMAIN}`.length);
    return `${prefix}-${environment}.${ROOT_DOMAIN}`;
  }

  return host;
}

/** The API base URL for a given environment (matches the CDK ALB domains). */
export function getApiBaseUrl(environment: ApiEnvironment): string {
  if (environment === 'local') return DEV_API_URL;
  if (environment === 'prod') return `https://api.${ROOT_DOMAIN}`;
  return `https://api-${environment}.${ROOT_DOMAIN}`;
}

/**
 * All portal origins that should be trusted by the API (Better Auth) for a
 * given environment. Local dev origins are always included so a developer can
 * point a local portal at any API. Non-prod environments derive their portal
 * origins from the deterministic `<prefix>-<env>.cbs-infinity.com` scheme,
 * plus `staging.portal.ctpa.com` on staging.
 */
export function getTrustedPortalOrigins(environment: ApiEnvironment): string[] {
  if (environment === 'local') return [...DEV_PORTAL_ORIGINS];
  if (environment === 'prod') return [...DEV_PORTAL_ORIGINS, ...PROD_PORTAL_ORIGINS];

  const suffix = `-${environment}`;
  return [
    ...DEV_PORTAL_ORIGINS,
    `https://${environment}.${ROOT_DOMAIN}`, // internal portal
    ...(environment === 'staging' ? [`https://${CTPA_INTERNAL_PORTAL_STAGING_DOMAIN}`] : []),
    ...CLIENT_PORTAL_SUBDOMAINS.map((prefix) => `https://${prefix}${suffix}.${ROOT_DOMAIN}`),
  ];
}

/**
 * Local dev port registry. The API listens on 4000, the jobs server on 4100,
 * the internal + client portals on 9000–9007, and the dockerized Postgres on
 * 5433 (main) / 5434 (shadow).
 */
export const DEV_PORTS = {
  API: 4000,
  JOBS: 4100,
  PORTAL_FIRST: 9000,
  PORTAL_LAST: 9007,
  POSTGRES: 5433,
  POSTGRES_SHADOW: 5434,
} as const;

/** Every local portal origin (`http://localhost:9000`..`9007`). */
export const DEV_PORTAL_ORIGINS: readonly string[] = Array.from(
  {length: DEV_PORTS.PORTAL_LAST - DEV_PORTS.PORTAL_FIRST + 1},
  (_, i) => `http://localhost:${DEV_PORTS.PORTAL_FIRST + i}`,
);

/** Generic URL query-param keys shared by the table/list state helpers. */
export const URL_QUERY_KEYS = {
  PAGE: 'page',
  PAGE_SIZE: 'pageSize',
  SEARCH: 'search',
  SORT: 'sort',
} as const;
